import { AutoAwesomeOutlined, QuestionAnswerOutlined } from "@mui/icons-material";
import { Box, Button, SxProps, Typography } from "@mui/material";
import { useAtom } from "jotai";
import { ChatInteraction, FocusedInteraction } from "../models";
import { scrollQuestionIntoView } from "../../../utils/scrollQuestionIntoView";
import { grey, indigo } from "../../../colors";

const ThumbStyle: SxProps = {
  verticalAlign: "sub",
  fill: grey[300],
  cursor: "pointer",
  "&:hover": {
    fill: grey[500],
  },
  "&.selected": {
    fill: indigo[900],
  },
};

interface ChatSessionOutlineProps {
  onClickNewThread: () => void;
  messages: ChatInteraction[];
  invocationType?: "qna" | "user" | undefined;
  onClickBackToStudy?: () => void;
}

export default function ChatSessionOutline({
  messages,
  onClickNewThread,
  invocationType = "user",
  onClickBackToStudy,
}: ChatSessionOutlineProps) {
  const [focusedQuestion] = useAtom(FocusedInteraction);

  return (
    <>
      <Box sx={{ display: "flex", alignItems: "center", gap: "0.5em" }}>
        <QuestionAnswerOutlined sx={{ fill: indigo[900], fontSize: 20 }} />
        <Typography sx={{ fontSize: 18, fontWeight: 600, color: indigo[900] }}>Current Chat</Typography>
      </Box>

      <Box
        sx={{
          overflowY: "auto",
          pr: "0.5em",
          flex: 1,
          minHeight: 0,
          "& .thread-question": {
            marginBottom: "0.5em",
            p: "0.5em 0.75em",
            borderRadius: "8px",
            fontSize: 13,
            color: indigo[700],
            cursor: "pointer",
            transition: "background-color 0.15s ease",
            "&:hover": { backgroundColor: grey[100] },
            "&.focused": {
              backgroundColor: indigo[50],
              borderLeft: `2px solid ${indigo[700]}`,
              color: indigo[900],
              fontWeight: 500,
            },
          },
        }}
      >
        {messages.map((response, i) => {
          const classes = ["thread-question"];
          if (focusedQuestion === response) {
            classes.push("focused");
          }

          // NOTE: This only works because ${i} is the same here that it is in <SessionThread />
          const anchorId = `${String(response.user_chat_session)}-${i}`;
          return (
            <Box key={i} className={classes.join(" ")} onClick={() => scrollQuestionIntoView(anchorId)}>
              {response.user_query}
            </Box>
          );
        })}
      </Box>

      <Box sx={{ textAlign: "center" }}>
        {invocationType === "user" && (
          <Button
            variant="contained"
            disableElevation
            sx={{
              backgroundColor: indigo[700],
              color: "#fff",
              marginBottom: "1em",
              width: "100%",
              textTransform: "none",
              fontWeight: 500,
              borderRadius: "8px",
              justifyContent: "center",
              alignItems: "center",
              "&:hover": { backgroundColor: indigo[800] },
            }}
            startIcon={<AutoAwesomeOutlined sx={{ fill: "#fff" }} />}
            onClick={() => onClickNewThread()}
          >
            Start a New Question Thread
          </Button>
        )}

        <Button
          variant="outlined"
          sx={{
            color: indigo[700],
            borderColor: indigo[200],
            width: "100%",
            textTransform: "none",
            fontWeight: 500,
            borderRadius: "8px",
            display: invocationType === "user" ? "none" : "flex",
            justifyContent: "center",
            alignItems: "center",
            "&:hover": { borderColor: indigo[700], backgroundColor: indigo[50] },
          }}
          onClick={onClickBackToStudy}
        >
          Back to Study
        </Button>
      </Box>
    </>
  );
}
